/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable @typescript-eslint/naming-convention */
import { useUser } from '@clerk/clerk-react';
import * as EventTracker from '@src/analytics/EventTracker';
import { fieldStateCss } from '@src/components/common/fieldStateStyles';
import {
  useDatoClient,
  useTemplatedPrompts,
  useUserIdentity,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import SimplePencilIcon from '@src/svgs/SimplePencilIcon';
import { ContentTab } from '@src/types.ts/general';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import {
  buildLengthInstruction,
  EMPTY_LENGTH_TARGET,
  parseRevisionLengthIntent,
} from '@src/utility/revisionLengthParser';
import { latestSavedEntry } from '@src/utility/savedContent';
import { extractTitleFromContent } from '@src/utility/strings';
import { format } from 'date-fns';
import { Jodit } from 'jodit-react';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { useAutosave } from '../../../hooks/useAutosave';
import { useContentAutosaveService } from '../../../hooks/useContentAutosaveService';
import { useContentVersioning } from '../../../hooks/useContentVersioning';
import ChatGPTService from '../../../services/ChatGPTService';
import {
  handleCopyToClipboard,
  handleDeleteBlogContent,
  handleRenameGeneratedContent,
  initializeContentStudioContent,
} from '../../../utility/general';
import {
  buildSpeakersFromMappings,
  extractSpeakerLabelsFromTranscriptElements,
  fetchDatoSpeakers,
  fetchSpeakerVoiceMappings,
} from '../../../utility/speakerUtils';
import { useFlagsCombination } from '../../../utility/useFlagsCombination';
import { AutosaveStatusIcon } from '../../common/AutosaveStatusIcon';
import { AutosaveStatusIndicator } from '../../common/AutosaveStatusIndicator';
import {
  ModalButtonsContainer,
  ModalCancelButton,
  ModalConfirmButton,
  ModalHeading,
  ModalWrapper,
} from '../../common/ConfirmModal';
import ContentShare from '../../common/ContentShare';
import Modal from '../../common/Modal';
import { SkeletonDocument } from '../../common/SkeletonDocument';
import { useOutsideAlerter } from '../../transcript/useClickOutside';
import AIProducerModal from '../AIProducerModal';
import BrandVoiceRevisionModal from '../BrandVoiceRevisionModal';
import ContentActions from '../ContentActions';
import ArticlesTable from './ArticlesTable';
import BlogEditor from './BlogEditor';
import ChatInterface from './ChatInterface';
import ContentVersionTracker from './ContentVersionTracker';
import SocialPreview from './SocialPreview';
import { useBlogProducer } from './useBlogProducer';
import { usePromptChannelSlugs } from './usePromptChannelSlugs';

import type { BlogOrEmailContent } from '../../../types.ts/general';
import type {
  BlogVersionHistory,
  BlogVersionHistoryItem,
  ContentViewData,
} from '../../../types.ts/story';
import type { Speaker } from '../SpeakerTypes';
import type { ContentVersion } from './ContentVersionTracker';
import type { AudienceOption } from '@src/components/common/AudienceSelector';
import type { GoalOption } from '@src/components/common/GoalSelector';
import type { AIPrompt } from '@src/types.ts/ai_prompts';
import type { LengthTargetState } from '@src/utility/revisionLengthParser';

const logger = LogManager.getLogger('feature:BlogView');

const getContentStats = (html: string | null) => {
  if (!html) return { words: 0, chars: 0 };
  // Strip HTML tags to get plain text
  const plainText = html
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const chars = plainText.length;
  const words = plainText
    ? plainText.split(/\s+/).filter(word => word.length > 0).length
    : 0;
  return { words, chars };
};

// For blog entries, strip the theme prefix and date suffix from "Theme - Title - Date"
const parseArticleTitle = (fullTitle: string, type: string): string => {
  if (type !== 'blog') return fullTitle;
  const parts = fullTitle.split(' - ');
  if (parts.length >= 3) {
    return parts.slice(1, -1).join(' - ');
  }
  return fullTitle;
};

// Normalize text for comparison - handles curly quotes, apostrophes, dashes, etc.
const normalizeForComparison = (text: string): string => {
  return (
    text
      // Normalize quotes
      .replace(/[\u2018\u2019\u201A\u201B\u0060\u00B4]/g, "'") // Single quotes/apostrophes
      .replace(/[\u201C\u201D\u201E\u201F\u00AB\u00BB]/g, '"') // Double quotes
      // Normalize dashes
      .replace(/[\u2013\u2014\u2015]/g, '-') // En-dash, em-dash, horizontal bar
      // Normalize spaces
      .replace(/[\u00A0\u2000-\u200A\u202F\u205F]/g, ' ') // Non-breaking and various spaces
      // Normalize ellipsis
      .replace(/\u2026/g, '...')
  );
};

// Diff algorithm that preserves HTML structure and shows changes in context
const generateDiffHtml = (oldHtml: string, newHtml: string): string => {
  // Parse HTML to extract text tokens while preserving tag positions
  const tokenize = (
    html: string,
  ): { tokens: string[]; isTag: boolean[]; tagType: string[] } => {
    const tokens: string[] = [];
    const isTag: boolean[] = [];
    const tagType: string[] = []; // Track tag names for structure preservation
    let current = '';
    let inTag = false;

    for (let i = 0; i < html.length; i++) {
      const char = html[i];
      if (char === '<') {
        if (current.trim()) {
          // Split text content into words
          const words = current.split(/(\s+)/);
          for (const word of words) {
            if (word) {
              tokens.push(word);
              isTag.push(false);
              tagType.push('');
            }
          }
        }
        current = '<';
        inTag = true;
      } else if (char === '>' && inTag) {
        current += '>';
        tokens.push(current);
        isTag.push(true);
        // Extract tag name (e.g., "p" from "<p>" or "/p" from "</p>")
        const tagMatch = current.match(/<\/?(\w+)/);
        tagType.push(tagMatch ? tagMatch[1].toLowerCase() : '');
        current = '';
        inTag = false;
      } else {
        current += char;
      }
    }
    if (current.trim()) {
      const words = current.split(/(\s+)/);
      for (const word of words) {
        if (word) {
          tokens.push(word);
          isTag.push(false);
          tagType.push('');
        }
      }
    }
    return { tokens, isTag, tagType };
  };

  const oldParsed = tokenize(oldHtml);
  const newParsed = tokenize(newHtml);

  // Extract text tokens with their surrounding structure info from old HTML
  const oldTextTokens: {
    text: string;
    index: number;
    precedingTags: string[];
  }[] = [];
  const newTextTokens: { text: string; index: number }[] = [];

  // For old tokens, track preceding structural tags (p, br, h1-h6, etc.)
  let pendingTags: string[] = [];
  oldParsed.tokens.forEach((token, i) => {
    if (oldParsed.isTag[i]) {
      const tag = oldParsed.tagType[i];
      // Track block-level and line-break tags
      if (
        ['p', 'br', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div', 'li'].includes(
          tag,
        )
      ) {
        pendingTags.push(token);
      }
    } else if (token.trim()) {
      oldTextTokens.push({
        text: token,
        index: i,
        precedingTags: [...pendingTags],
      });
      pendingTags = [];
    }
  });

  newParsed.tokens.forEach((token, i) => {
    if (!newParsed.isTag[i] && token.trim()) {
      newTextTokens.push({ text: token, index: i });
    }
  });

  // LCS-based diff on text tokens only (using normalized text for comparison)
  const m = oldTextTokens.length;
  const n = newTextTokens.length;
  const lcs: number[][] = [];

  // Helper to compare tokens using normalized text
  const tokensMatch = (oldToken: string, newToken: string): boolean => {
    return (
      normalizeForComparison(oldToken) === normalizeForComparison(newToken)
    );
  };

  for (let i = 0; i <= m; i++) {
    lcs[i] = [];
    for (let j = 0; j <= n; j++) {
      if (i === 0 || j === 0) {
        lcs[i][j] = 0;
      } else if (
        tokensMatch(oldTextTokens[i - 1].text, newTextTokens[j - 1].text)
      ) {
        lcs[i][j] = lcs[i - 1][j - 1] + 1;
      } else {
        lcs[i][j] = Math.max(lcs[i - 1][j], lcs[i][j - 1]);
      }
    }
  }

  // Backtrack to find diff operations, preserving structure info for removed items
  const diff: {
    type: 'same' | 'removed' | 'added';
    text: string;
    precedingTags?: string[];
  }[] = [];
  let i = m;
  let j = n;

  while (i > 0 || j > 0) {
    if (
      i > 0 &&
      j > 0 &&
      tokensMatch(oldTextTokens[i - 1].text, newTextTokens[j - 1].text)
    ) {
      // Use the new text version when tokens match (preserves the newer formatting)
      diff.unshift({ type: 'same', text: newTextTokens[j - 1].text });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || lcs[i][j - 1] >= lcs[i - 1][j])) {
      diff.unshift({ type: 'added', text: newTextTokens[j - 1].text });
      j--;
    } else {
      diff.unshift({
        type: 'removed',
        text: oldTextTokens[i - 1].text,
        precedingTags: oldTextTokens[i - 1].precedingTags,
      });
      i--;
    }
  }

  // Now rebuild HTML using the new HTML structure but marking changes
  let result = '';

  // Legend header
  result +=
    '<div style="margin-bottom: 16px; padding: 12px; background: #f5f5f5; border-radius: 8px; font-size: 12px; color: #666;">';
  result += '<strong>Comparison View</strong> (Selected Version → Latest): ';
  result +=
    '<span style="background: #ffcdd2; padding: 2px 6px; border-radius: 3px; text-decoration: line-through; margin: 0 4px;">Removed</span>';
  result +=
    '<span style="background: #c8e6c9; padding: 2px 6px; border-radius: 3px; margin: 0 4px;">Added</span>';
  result += '</div>';

  // Process the new HTML and mark changes inline
  let diffPos = 0;

  // Helper to render removed tokens with their structure
  const renderRemovedTokens = (): string => {
    let removedHtml = '';
    let isFirstRemoved = true;

    while (diffPos < diff.length && diff[diffPos].type === 'removed') {
      const item = diff[diffPos];

      // Add line break before removed content if it had preceding paragraph/break tags
      // and it's not the first removed item in a sequence
      if (item.precedingTags && item.precedingTags.length > 0) {
        // Check if there's a paragraph or line break tag
        const hasLineBreak = item.precedingTags.some(
          tag =>
            tag.includes('<p') ||
            tag.includes('<br') ||
            tag.includes('<h') ||
            tag.includes('<div') ||
            tag.includes('<li'),
        );
        if (hasLineBreak && !isFirstRemoved) {
          removedHtml += '<br/>';
        }
      }

      removedHtml += `<span style="background: #ffcdd2; text-decoration: line-through; padding: 1px 3px; border-radius: 2px; color: #c62828;">${item.text}</span> `;
      isFirstRemoved = false;
      diffPos++;
    }

    return removedHtml;
  };

  for (let idx = 0; idx < newParsed.tokens.length; idx++) {
    const token = newParsed.tokens[idx];
    const isTagToken = newParsed.isTag[idx];

    if (isTagToken) {
      result += token;
    } else if (token.trim()) {
      // Insert any removed text before current position
      result += renderRemovedTokens();

      if (diffPos < diff.length) {
        if (diff[diffPos].type === 'added') {
          result += `<span style="background: #c8e6c9; padding: 1px 3px; border-radius: 2px; color: #2e7d32;">${token}</span>`;
          diffPos++;
        } else if (diff[diffPos].type === 'same') {
          result += token;
          diffPos++;
        }
      } else {
        result += token;
      }
    } else {
      // Whitespace
      result += token;
    }
  }

  // Add any remaining removed items at the end
  const remainingRemoved = renderRemovedTokens();
  if (remainingRemoved) {
    result += ' ' + remainingRemoved;
  }

  return result;
};

const getContentTitleFromTemplate = (
  template?: AIPrompt,
  contentHtml?: string,
  customPrompt?: string,
) => {
  const date = format(new Date(), 'yyyy-MM-dd');

  // Determine template title
  let templateTitle = template?.title;
  if (customPrompt) {
    const customWords = customPrompt
      .trim()
      .split(/\s+/)
      .slice(0, 6)
      .join(' ')
      .replace(/[.,;!?]+$/, ''); // Remove trailing punctuation
    templateTitle = customWords || template?.title;
  }

  // If content is provided, extract article title
  if (contentHtml) {
    const articleTitle = extractTitleFromContent(contentHtml);
    if (articleTitle) {
      return `${templateTitle} - ${articleTitle} - ${date}`;
    }
  }

  // Fallback to original format
  return `${templateTitle} - ${date}`;
};

const BlogView = observer(
  ({ type = 'blog' }: { type: 'blog' | 'socialPost' }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [lengthTarget, setLengthTarget] =
      useState<LengthTargetState>(EMPTY_LENGTH_TARGET);
    const [selectedSpeakers, setSelectedSpeakers] = useState<Speaker[]>([]);
    const [selectedGoal, setSelectedGoal] = useState<GoalOption | null>(null);
    const [selectedAudiences, setSelectedAudiences] = useState<
      AudienceOption[]
    >([]);
    const [contentHtml, setContentHtml] = useState<string | null>(null);
    const [isEditorDirty, setIsEditorDirty] = useState(false);
    const isUserEditDirtyRef = useRef(false); // true only for manual user edits, not AI revisions
    const suppressEditorDirtyUntilRef = useRef(0); // timestamp — ignore onEditorDirty until this time
    /** Set content programmatically and suppress false onEditorDirty for 1s */
    const setContentHtmlProgrammatic = useCallback((html: string | null) => {
      suppressEditorDirtyUntilRef.current = Date.now() + 1000;
      setContentHtml(html);
    }, []);
    const resetDirtyState = useCallback(() => {
      setIsEditorDirty(false);
      isUserEditDirtyRef.current = false;
    }, []);
    const [viewMode, setViewMode] = useState<'edit' | 'preview'>('preview');
    // Distribution channel of the prompt behind the current social post, so the
    // preview opens on the platform it was written for (PLAT-11038). Null for
    // custom prompts and for prompts with no channel — both show the default.
    const [channelSlug, setChannelSlug] = useState<string | null>(null);
    const [showArticlesList, setShowArticlesList] = useState(true);
    const [switcherOpen, setSwitcherOpen] = useState(false);
    const switcherRef = useRef<HTMLDivElement>(null);
    useOutsideAlerter(switcherRef, () => setSwitcherOpen(false));
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [editingTitleValue, setEditingTitleValue] = useState('');
    const titleInputRef = useRef<HTMLInputElement>(null);
    const isCreatingNewRef = useRef(false);
    // PR4 analytics: store the topics applied at the last Generate so the
    // subsequent Regenerate event can include them too.
    const latestSelectedTopicsRef = useRef<string[] | undefined>(undefined);
    const editorScrollRef = useRef<HTMLDivElement>(null);
    const videoCreator = useVideoCreatorStore();
    const userIdentity = useUserIdentity();
    const datoClientStore = useDatoClient();
    const [searchParams, setSearchParams] = useSearchParams();
    const gptServiceRef = useRef(
      new ChatGPTService(videoCreator, datoClientStore.aiPromptRepository),
    );
    const gptService = gptServiceRef.current;

    const { story } = videoCreator;
    const [imageSize, setImageSize] = useState<Record<
      string,
      Record<'width' | 'height', number>
    > | null>(null);
    // `isSaving` is now declared alongside the legacy `handleSave` below (flag-gated).
    const [lastSavedContent, setLastSavedContent] = useState<string | null>(
      null,
    );
    const [openedFromTable, setOpenedFromTable] = useState(false);
    const panelRef = useRef<HTMLDivElement>(null);
    const [isComparing, setIsComparing] = useState(false);
    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
    const [transcriptSpeakers, setTranscriptSpeakers] = useState<Speaker[]>([]);
    // Track if component has finished initial mount to prevent flash
    const [isInitialized, setIsInitialized] = useState(false);
    useEffect(() => {
      setIsInitialized(true);
    }, []);

    useEffect(() => {
      if (openedFromTable && !showArticlesList) {
        window.scrollTo(0, 360);
      }
    }, [openedFromTable, showArticlesList]);

    const [promptTemplate, setPromptTemplate] = useState<
      AIPrompt | undefined
    >();
    const [customPrompt, setCustomPrompt] = useState<string | undefined>();

    const contentProducer = useBlogProducer(type);
    const contentAutosave = useContentAutosaveService();

    // Use central content versioning hook
    const {
      activeVersions: activeArticleVersions,
      currentVersionId,
      latestVersion,
      isViewingPastVersion,
      versionCounter,
      saveVersion: saveVersionHook,
      saveNamedVersion,
      selectVersion,
      startAIGeneration,
      endAIGeneration,
      resetVersions,
      setVersions,
      renameVersion,
      deleteVersion,
    } = useContentVersioning({
      // Versioning is strictly user-initiated (Save as version) or AI-initiated
      // (endAIGeneration). Autosave is invisible and does not create versions.
      onVersionSaved: async versions => {
        // Automatically persist versions to backend whenever they change
        if (!currentContent?.id || !story?.id) return;

        try {
          const contentId = currentContent.id;
          const savedData =
            type === 'blog' ? story.savedBlog : story.savedSocialPost;

          if (!savedData || !savedData[contentId]) return;

          // Convert ContentVersion[] to BlogVersionHistory format
          const versionHistory: BlogVersionHistory = {
            versions: versions.map(v => ({
              id: v.id,
              content: v.content,
              timestamp: v.timestamp.toISOString(),
              label: v.label,
              versionNumber: v.versionNumber,
              actor: v.actor,
              username: v.username || userInfo?.user?.fullName || 'Unknown',
              deleted: v.deleted || undefined,
              prompt: v.prompt,
              isNamed: v.isNamed || undefined,
              generationDetails: v.generationDetails,
            })),
            versionCounter: versionCounter,
          };

          // Update the saved content with new version history
          const updatedSavedData = {
            ...savedData,
            [contentId]: {
              ...savedData[contentId],
              version_history: versionHistory,
            },
          };

          // Save to backend via the ContentAutosaveService so components
          // never call storyRepository directly (CLAUDE.md boundary rule).
          const saveResult = await contentAutosave.saveAiGenerated({
            storyId: story.id,
            data: updatedSavedData,
            type: type === 'blog' ? 'saved_blog' : 'saved_social_post',
          });
          if (!saveResult.ok) throw saveResult.error;

          // Update local state
          const contentKey = type === 'blog' ? 'savedBlog' : 'savedSocialPost';
          videoCreator.story![contentKey] = updatedSavedData;
        } catch (error) {
          logger.error('Error saving version history:', error);
        }
      },
    });

    const userInfo = useUser();
    const selectedContent = videoCreator.selectedGeneratedContent[type];
    const {
      contentStudioEnableBlogSave,
      contentStudioEnableBlogShare,
      contentStudioContentProducingEnableCustom,
      contentStudioContentProducingEnableGenerate,
      contentStudioEnableAutosave,
    } = useFlagsCombination(userIdentity.currentRole);

    const savedContent =
      type === 'blog'
        ? videoCreator.story?.savedBlog
        : videoCreator.story?.savedSocialPost;

    const entries = contentProducer.getEntries();
    const visibleEntries = entries;
    const [currentContent, setCurrentContent] =
      useState<BlogOrEmailContent | null>(null);
    const loadedVersionsForArticleRef = useRef<number | null>(null);

    // ---- Autosave ------------------------------------------------------------
    // Idle-based autosave that replaces the old manual Save/Update button.
    // - If `currentContent.id` exists → overwrite the saved entry in-place
    //   (repository.autosaveBlogOrEmail).
    // - Otherwise → first-save path: create the entry via BlogProducer, then
    //   subsequent autosaves land in-place.
    const currentContentRef = useRef(currentContent);
    currentContentRef.current = currentContent;

    const autosaveFn = useCallback(
      async (content: string) => {
        if (!contentStudioEnableAutosave) return;
        if (!story?.id) return;
        const cc = currentContentRef.current;
        const name = userInfo?.user?.fullName ?? null;
        const contentTitle = getContentTitleFromTemplate(
          promptTemplate,
          content,
          promptTemplate ? undefined : customPrompt,
        );

        if (cc?.id) {
          // In-place upsert — never creates a new entry or a new version.
          const result = await contentAutosave.autosaveInPlace({
            story: {
              id: story.id,
              savedBlog: story.savedBlog,
              savedSocialPost: story.savedSocialPost,
              savedEmail: story.savedEmail,
            },
            content,
            title: cc.title || contentTitle,
            userName: name,
            type: type === 'blog' ? 'saved_blog' : 'saved_social_post',
            replaceKey: cc.id,
          });
          // Successful in-place update — nothing else to do.
          if (result.ok) return;
          // Entry was removed (e.g. deleted in another tab) — fall through
          // to the first-save path below so the content isn't silently lost.
          if (result.error.code !== 'NOT_FOUND') throw result.error;
        }

        // First save (or NOT_FOUND fallback) — create a new entry.
        const data = await contentProducer.saveBlogContent(
          name ?? undefined,
          content,
          contentTitle,
          undefined,
          false,
        );
        if (data) {
          setCurrentContent({
            id: data.id,
            title: data.title,
            content: data.content,
            username: data.username,
          });
        }
      },
      [
        contentStudioEnableAutosave,
        story,
        userInfo?.user?.fullName,
        type,
        contentProducer,
        contentAutosave,
        promptTemplate,
        customPrompt,
      ],
    );

    // Disable autosave while generation is in progress. The streamed content
    // is mid-flight and may be replaced wholesale on error; persisting a
    // snapshot of it would create junk drafts (PLAT-10121).
    const autosave = useAutosave({
      save: autosaveFn,
      debounceMs: 2000,
      enabled: contentStudioEnableAutosave && !isLoading && !isRegenerating,
    });

    // Keep the legacy dirty flag in sync with autosave status so the
    // SaveOrDiscardModal exit guard doesn't fire after autosave has
    // persisted the draft.
    useEffect(() => {
      if (autosave.status === 'saved') {
        setIsEditorDirty(false);
        isUserEditDirtyRef.current = false;
      }
    }, [autosave.status]);

    // Reset autosave dedup state when the user switches to a different saved
    // entry — prevents the previous entry's lastSavedContentRef from masking
    // a legitimate change on the new entry. `autosave` is stable within a
    // render; only id should trigger this reset.
    const autosaveResetRef = useRef(autosave.reset);
    autosaveResetRef.current = autosave.reset;
    useEffect(() => {
      autosaveResetRef.current();
    }, [currentContent?.id]);

    const canSaveAsVersion =
      contentStudioEnableBlogSave &&
      !!selectedContent?.type &&
      contentStudioEnableAutosave;

    const handleSaveAsVersion = useCallback(
      async (name: string) => {
        // Flush the draft first so the named version snapshots the
        // persisted content.
        await autosave.flush();
        saveNamedVersion(
          contentHtml || '',
          name,
          userInfo?.user?.fullName || undefined,
        );
        if (story?.id) {
          if (type === 'blog') {
            EventTracker.trackArticleSaved({ story_id: story.id });
          } else {
            EventTracker.trackSocialPostSaved({ story_id: story.id });
          }
        }
        videoCreator.showToast({
          state: 'success',
          message: `Version saved as "${name}"`,
        });
      },
      [
        autosave,
        saveNamedVersion,
        contentHtml,
        userInfo?.user?.fullName,
        story?.id,
        type,
        videoCreator,
      ],
    );

    const typeKey = type === 'blog' ? 'Blog' : 'Social Post';
    useEffect(() => {
      initializeContentStudioContent(videoCreator, story?.id, typeKey);
    }, [story?.id]);

    // Restore last selected content when component mounts or when saved content changes
    useEffect(() => {
      // Skip auto-restore while creating new content
      if (isCreatingNewRef.current) return;
      // If currentContent is already set, don't override it
      if (currentContent || !savedContent) return;

      // Priority 1: URL contentKey parameter (deep-link from Recent Content)
      const urlContentKey = searchParams.get('contentKey');
      if (urlContentKey) {
        const key = parseInt(urlContentKey, 10);
        const savedItem = savedContent[key];
        if (savedItem) {
          setCurrentContent({
            id: key,
            title: savedItem.title,
            content: savedItem.content,
            username: savedItem.username,
          });
          // Open directly into the editor view, not the list
          setShowArticlesList(false);
          setLastSavedContent(savedItem.content);
          resetDirtyState();
          // Clear the contentKey from URL after consuming it
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('contentKey');
          setSearchParams(newParams, { replace: true });
          return;
        }
      }

      // Priority 2: selectedGeneratedContent (session state)
      const selectedId = selectedContent?.id;
      if (selectedId && selectedContent.type === 'saved') {
        const id = parseInt(selectedId, 10);
        const savedItem = savedContent[id];
        if (savedItem) {
          setCurrentContent({
            id,
            title: savedItem.title,
            content: savedItem.content,
            username: savedItem.username,
          });
          return;
        }
      }

      // Priority 3: Fallback to most recent entry
      const latest = latestSavedEntry(savedContent);
      if (latest) {
        setCurrentContent({
          id: latest.id,
          title: latest.entry.title,
          content: latest.entry.content,
          username: latest.entry.username,
        });
      }
    }, [savedContent, currentContent, selectedContent, searchParams]);

    // Persist selected content to videoCreator state when it changes
    useEffect(() => {
      if (currentContent?.id) {
        videoCreator.selectedGeneratedContent = {
          ...videoCreator.selectedGeneratedContent,
          [type]: {
            id: currentContent.id.toString(),
            type: 'saved',
            content: currentContent.title,
          },
        };
      }
    }, [currentContent?.id]);

    // Reset comparison mode when loading different content
    useEffect(() => {
      setIsComparing(false);
    }, [currentContent?.id, selectedContent?.id]);

    // Derive transcript readiness in render scope so MobX observer tracks it
    const hasTranscript =
      (videoCreator.rootStore?.transcriptionStore?.transcriptionElements
        ?.length ?? 0) > 0;

    // Load speakers from transcript
    useEffect(() => {
      if (!story?.id || !hasTranscript) return;

      const loadSpeakers = async () => {
        try {
          // Extract speaker labels from transcript
          const transcriptElements =
            videoCreator.rootStore?.transcriptionStore?.getFinalTranscriptionElements();
          const speakerLabels =
            extractSpeakerLabelsFromTranscriptElements(transcriptElements);

          if (speakerLabels.length === 0) {
            return;
          }

          // Fetch mappings and Dato speakers
          const [speakerMappings, datoSpeakers] = await Promise.all([
            fetchSpeakerVoiceMappings(story.id),
            fetchDatoSpeakers(story.id, datoClientStore),
          ]);

          // Build the complete speaker list
          const speakerList = buildSpeakersFromMappings(
            speakerLabels,
            speakerMappings,
            datoSpeakers,
          );

          setTranscriptSpeakers(speakerList);
        } catch (error) {
          logger.error('Error loading speaker data:', error);
        }
      };

      loadSpeakers();
    }, [story?.id, datoClientStore, hasTranscript]);

    const data = videoCreator.contentStudioGeneratedContent?.[typeKey];

    const hasBeenGenerated = data?.hasBeenGenerated;
    const generatedContent = data?.content?.response || '';
    const generatedContentLanguage = data?.selectedLanguage || null;

    const handleCopy = (copyType: 'text' | 'html') => {
      if (!contentHtml) return;

      if (copyType === 'html') {
        handleCopyToClipboard(contentHtml, 'html');
      } else {
        const plainText = Jodit.modules.Helpers.stripTags(contentHtml);
        handleCopyToClipboard(plainText, 'plain');
      }

      if (type === 'blog') {
        EventTracker.trackArticleCopied({
          story_id: videoCreator.story?.id,
          copy_type: copyType,
        });
      } else {
        EventTracker.trackSocialPostCopied({
          story_id: videoCreator.story?.id,
          copy_type: copyType,
        });
      }
    };

    // Legacy manual Save/Update handler — retained behind the
    // `contentStudioEnableAutosave` LaunchDarkly flag. When autosave is ON this
    // is unused; when autosave is OFF this is the only save path. Delete once
    // the flag is fully rolled out.
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const handleSave = async () => {
      if (!story?.id || !contentHtml) return;

      const isUpdate = lastSavedContent !== null;

      if (type === 'blog') {
        EventTracker.trackArticleSaved({ story_id: story.id });
      } else {
        EventTracker.trackSocialPostSaved({ story_id: story.id });
      }

      setIsSaving(true);
      const name = userInfo?.user?.fullName;

      const contentTitle = getContentTitleFromTemplate(
        promptTemplate,
        contentHtml,
        promptTemplate ? undefined : customPrompt,
      );

      const data = await contentProducer.saveBlogContent(
        name,
        contentHtml,
        contentTitle,
        currentContent?.id,
        true,
        type === 'socialPost' ? channelSlug : null,
      );

      if (data) {
        setCurrentContent({
          id: data.id,
          title: data.title,
          content: data.content,
          username: data.username,
        });
        resetDirtyState();
        setLastSavedContent(contentHtml);
        saveVersionHook(contentHtml, 'user_revision', {
          prompt: 'Manual save',
          generationDetails: buildGenerationDetails(),
          username: userInfo?.user?.fullName || undefined,
        });
        if (videoCreator.contentStudioGeneratedContent?.[typeKey]) {
          videoCreator.contentStudioGeneratedContent[typeKey].hasBeenGenerated =
            false;
        }
      }

      setIsSaving(false);

      if (data) {
        videoCreator.showToast({
          state: 'success',
          message: isUpdate
            ? `${type === 'blog' ? 'Article' : 'Social Post'} updated successfully`
            : `${type === 'blog' ? 'Article' : 'Social Post'} saved successfully`,
        });
      }
    };

    const handleStartEditTitle = useCallback(() => {
      if (!currentContent) return;
      const parsed = parseArticleTitle(
        currentContent.title || 'Untitled',
        type,
      );
      setEditingTitleValue(parsed);
      setIsEditingTitle(true);
      setTimeout(() => titleInputRef.current?.select(), 0);
    }, [currentContent, type]);

    const handleRenameTitle = useCallback(
      async (contentId: number, newDisplayTitle: string) => {
        const entry = entries.find(e => e.id === contentId);
        if (!entry) return;

        const trimmed = newDisplayTitle.trim();
        const currentParsed = parseArticleTitle(entry.title || '', type);
        if (!trimmed || trimmed === currentParsed) return;

        const parts = entry.title.split(' - ');
        let newFullTitle: string;
        if (type === 'blog' && parts.length >= 3) {
          newFullTitle = [parts[0], trimmed, parts[parts.length - 1]].join(
            ' - ',
          );
        } else {
          newFullTitle = trimmed;
        }

        try {
          await handleRenameGeneratedContent(
            videoCreator,
            datoClientStore.storyRepository,
            contentId,
            newFullTitle,
            type === 'blog' ? 'savedBlog' : 'savedSocialPost',
            type === 'blog' ? 'saved_blog' : 'saved_social_post',
            () => {},
            () => {},
            () => {},
            setCurrentContent,
            false,
          );

          // Update currentContent if we renamed the active article
          if (currentContent?.id === contentId) {
            setCurrentContent(prev =>
              prev ? { ...prev, title: newFullTitle } : prev,
            );
          }
        } catch (error) {
          logger.error(
            'Failed to rename content title',
            extractErrorInfo(error),
          );
        }
      },
      [
        entries,
        type,
        videoCreator,
        datoClientStore.storyRepository,
        currentContent,
      ],
    );

    const handleRenameCurrentTitle = useCallback(
      async (newDisplayTitle: string) => {
        setIsEditingTitle(false);
        if (!currentContent?.id) return;
        await handleRenameTitle(currentContent.id, newDisplayTitle);
      },
      [currentContent?.id, handleRenameTitle],
    );

    useEffect(() => {
      const generated_content = generatedContent as string;

      if (generatedContent && hasBeenGenerated) {
        const htmlData = contentProducer.produceHtmlFromGeneratedContent(
          generated_content,
          false,
          imageSize,
          null,
          null,
          null,
        );

        setContentHtmlProgrammatic(htmlData);
        return;
      }

      if (currentContent?.id && savedContent) {
        const htmlData = contentProducer.produceHtmlFromSavedContent(
          currentContent.id,
          savedContent,
        );
        setContentHtmlProgrammatic(htmlData || null);
        // Reopen on the channel the post was generated for (PLAT-11038); posts
        // saved before this shipped have no slug and fall back to the default.
        const entry = savedContent[currentContent.id] as
          | { channelSlug?: string }
          | undefined;
        setChannelSlug(entry?.channelSlug ?? null);
      }
    }, [currentContent, savedContent, generatedContent]);

    // Auto-scroll the editor to the bottom as content streams in during generation.
    // Skipped for the social-post Preview: the rendered card isn't a streaming text
    // feed, so forced scroll-to-bottom would fight a user trying to scroll up.
    useEffect(() => {
      const isSocialPreview = type === 'socialPost' && viewMode === 'preview';
      if (
        isLoading &&
        contentHtml &&
        editorScrollRef.current &&
        !isSocialPreview
      ) {
        editorScrollRef.current.scrollTop =
          editorScrollRef.current.scrollHeight;
      }
    }, [contentHtml, isLoading, type, viewMode]);

    // Load version history from saved content when currentContent changes
    useEffect(() => {
      if (!currentContent?.id || !savedContent) {
        loadedVersionsForArticleRef.current = null;
        return;
      }

      const contentId = currentContent.id;

      // Skip reload if we already loaded versions for this article
      // (prevents infinite loop when our own persistence updates savedContent)
      if (loadedVersionsForArticleRef.current === contentId) {
        return;
      }

      const savedItem = savedContent[contentId];
      if (!savedItem) return;

      loadedVersionsForArticleRef.current = contentId;

      // Load persisted version history if it exists
      if (savedItem.version_history) {
        try {
          const history: BlogVersionHistory =
            typeof savedItem.version_history === 'string'
              ? JSON.parse(savedItem.version_history)
              : savedItem.version_history;

          if (history.versions && history.versions.length > 0) {
            // Convert stored versions to ContentVersion format
            const loadedVersions: ContentVersion[] = history.versions.map(
              (v: BlogVersionHistoryItem, index: number) => ({
                id: v.id,
                content: v.content,
                timestamp: new Date(v.timestamp),
                label: v.label,
                versionNumber:
                  v.versionNumber ?? history.versions.length - index,
                actor: v.actor ?? 'user',
                username: v.username,
                deleted: v.deleted,
                isNamed: v.isNamed,
                prompt: v.prompt,
                generationDetails: v.generationDetails,
              }),
            );

            // Load versions into the versioning system with persisted counter
            setVersions(
              loadedVersions,
              history.versionCounter || loadedVersions.length,
            );
          }
        } catch (error) {
          logger.error('Error loading version history:', error);
        }
      }
      // Articles without version_history simply have no versions until user clicks Save/Update
    }, [currentContent?.id, savedContent]);

    // Two-phase version creation for first AI generation:
    // Phase 1 (auto-save effect): sets needsInitialVersionRef=true after persisting to DatoCMS
    // Phase 2 (separate effect below): creates the version once currentContent.id is available
    // This split is necessary because onVersionSaved needs currentContent.id to persist versions.
    const needsInitialVersionRef = useRef(false);

    // Track when loading finishes to save a new version
    const wasLoadingRef = useRef(false);
    const pendingSaveRef = useRef<{
      pending: boolean;
      isRevision: boolean;
      prompt?: string;
    }>({
      pending: false,
      isRevision: false,
    });
    const latestPromptRef = useRef<string | undefined>(undefined);
    const latestTemplateNameRef = useRef<string | undefined>(undefined);

    // Track loading state for auto-save and generation completion
    useEffect(() => {
      if (isLoading) {
        pendingSaveRef.current = {
          pending: true,
          isRevision: isRegenerating,
          prompt: latestPromptRef.current,
        };
      } else if (wasLoadingRef.current && contentHtml) {
        // Loading just finished
        if (currentContent?.id) {
          // Article already saved (revision case) — create version immediately
          endAIGeneration(contentHtml, {
            generationDetails: buildGenerationDetails(),
            username: userInfo?.user?.fullName || undefined,
          });
        } else {
          // First generation — just end generation state; version created after auto-save
          endAIGeneration();
        }
        // If this was a revision (regenerate), mark editor dirty so Update button enables
        if (pendingSaveRef.current.isRevision) {
          setIsEditorDirty(true);
        }
      }
      wasLoadingRef.current = isLoading;
    }, [isLoading, isRegenerating, contentHtml, currentContent?.id]);

    // Auto-save content when first generated (before versions are saved)
    useEffect(() => {
      let cancelled = false;

      const autoSaveContent = async () => {
        // Only auto-save if we have pending content, no currentContent yet, and loading is complete
        if (
          pendingSaveRef.current.pending &&
          contentHtml &&
          !isLoading &&
          !currentContent?.id &&
          story?.id
        ) {
          const name = userInfo?.user?.fullName;
          const contentTitle = getContentTitleFromTemplate(
            promptTemplate,
            contentHtml,
            promptTemplate ? undefined : customPrompt,
          );

          try {
            const data = await contentProducer.saveBlogContent(
              name,
              contentHtml,
              contentTitle,
            );

            if (!cancelled && data) {
              const { id, title, content, username } = data;
              setCurrentContent({
                id,
                title,
                content,
                username,
              });
              needsInitialVersionRef.current = true;
            }
          } finally {
            if (!cancelled) {
              isCreatingNewRef.current = false;
            }
          }
        }
      };

      autoSaveContent();
      return () => {
        cancelled = true;
      };
    }, [
      contentHtml,
      isLoading,
      currentContent?.id,
      story?.id,
      userInfo?.user?.fullName,
      promptTemplate,
      customPrompt,
    ]);

    // Create the initial 'generated' version after auto-save sets currentContent.id
    // This must be a separate effect because onVersionSaved needs currentContent.id to persist
    useEffect(() => {
      if (
        needsInitialVersionRef.current &&
        currentContent?.id &&
        contentHtml &&
        !isLoading
      ) {
        needsInitialVersionRef.current = false;
        saveVersionHook(contentHtml, 'generated', {
          generationDetails: buildGenerationDetails(),
        });
      }
    }, [currentContent?.id, contentHtml, isLoading, saveVersionHook]);

    const templatedPromptsStore = useTemplatedPrompts();
    const [aiProducerOpen, setAiProducerOpen] = useState(false);
    // Prompt -> channel association, loaded once the producer opens (PLAT-11038).
    const promptChannelSlugs = usePromptChannelSlugs(
      type === 'socialPost' && aiProducerOpen,
    );

    // Auto-open AI producer when navigating with ?produce=true
    useEffect(() => {
      if (searchParams.get('produce') === 'true') {
        const activeTab = searchParams.get('tab');
        const isMyTab =
          (type === 'blog' && activeTab === ContentTab.blog) ||
          (type === 'socialPost' && activeTab === ContentTab.social_posts);
        if (isMyTab) {
          setAiProducerOpen(true);
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('produce');
          setSearchParams(newParams, { replace: true });
        }
      }
    }, [searchParams, setSearchParams, type]);

    const promptKey = type === 'blog' ? 'Blog' : 'Social Post';

    // Surface generation failures to the user via a toast rather than writing
    // the error string into the streamed-content channel (which would land in
    // the editor and be persisted by autosave — PLAT-10121).
    const handleGenerationError = useCallback(
      (_message: string) => {
        videoCreator.showToast({
          state: 'error',
          message: 'Generation failed, try again',
        });
      },
      [videoCreator],
    );

    const handleGenerate = async (
      template?: AIPrompt,
      brandVoice?: string,
      customInstructions?: string,
      avoidWords?: string,
      selectedSpeakers?: Speaker[],
      selectedGoal?: GoalOption | null,
      selectedAudiences?: AudienceOption[],
      selectedLanguage?: string,
      _selectedPhotoSources?: unknown,
      clipTranscript?: string,
      _speakerFilterActive?: boolean,
      selectedTopics?: string[],
    ) => {
      latestSelectedTopicsRef.current = selectedTopics;
      setImageSize(null);
      setIsLoading(true);
      setIsRegenerating(false);
      setLengthTarget(EMPTY_LENGTH_TARGET);
      setOpenedFromTable(false);
      setShowArticlesList(false);
      isCreatingNewRef.current = true;
      resetVersions(); // Reset version counter for new article
      startAIGeneration(false); // Track generation state to suppress user edit versions
      loadedVersionsForArticleRef.current = null;
      setLastSavedContent(null); // Reset so button shows "Save" for new article
      resetDirtyState();
      setCurrentContent(null);
      setContentHtmlProgrammatic(null); // Clear editor immediately so old content doesn't flash

      // Update state immediately before generation starts
      if (selectedSpeakers) setSelectedSpeakers(selectedSpeakers);
      if (selectedGoal) setSelectedGoal(selectedGoal);
      if (selectedAudiences) setSelectedAudiences(selectedAudiences);
      if (template) {
        setPromptTemplate(template);
        setCustomPrompt(undefined);
        latestTemplateNameRef.current = template.title;
      } else if (customInstructions) {
        setCustomPrompt(customInstructions);
        setPromptTemplate(undefined);
        latestTemplateNameRef.current = customInstructions.trim() || undefined;
      }

      // Open the preview for the prompt's channel (PLAT-11038). A custom prompt,
      // a prompt with no channel, or an unresolved association all give null,
      // which SocialPreview resolves to the default (LinkedIn) preview.
      setChannelSlug(
        template ? (promptChannelSlugs.get(template.id) ?? null) : null,
      );

      const genProps = {
        story_id: videoCreator.story?.id,
        story_title: videoCreator.story?.title,
        template: template?.title,
        custom_instructions: customInstructions,
        selected_language: selectedLanguage,
        goal: selectedGoal?.id,
        audience_count: selectedAudiences?.length,
        brand_voice: !!brandVoice,
        selected_topics: selectedTopics,
      };
      if (type === 'blog') {
        EventTracker.trackArticleGenerationStarted(genProps);
      } else {
        EventTracker.trackSocialPostGenerationStarted(genProps);
      }

      const contentTitle = type === 'blog' ? 'Blog' : 'Social Post';
      initializeContentStudioContent(
        videoCreator,
        videoCreator.story?.id,
        contentTitle,
      );

      // When generating from a clip, use the clip transcript as primary input
      // and pass the full story transcript as additional context
      try {
        if (clipTranscript) {
          await gptService.regenerateFromClipTranscript({
            key: promptKey,
            type: 'new',
            setLoading: setIsLoading,
            onError: handleGenerationError,
            template,
            customInstructions,
            brandVoice,
            avoidWords,
            selectedSpeakers,
            selectedGoal,
            selectedAudiences,
            selectedLanguage,
            clipTranscript,
          });
        } else {
          await gptService.regenerateStreamResponse({
            key: promptKey,
            type: 'new',
            setLoading: setIsLoading,
            onError: handleGenerationError,
            template,
            customInstructions,
            brandVoice,
            avoidWords,
            selectedSpeakers,
            selectedGoal,
            selectedAudiences,
            selectedLanguage,
          });
        }
        if (type === 'blog') {
          EventTracker.trackArticleGenerationCompleted({
            story_id: videoCreator.story?.id,
          });
        } else {
          EventTracker.trackSocialPostGenerationCompleted({
            story_id: videoCreator.story?.id,
          });
        }
      } catch (error) {
        const errMsg = error instanceof Error ? error.message : String(error);
        if (type === 'blog') {
          EventTracker.trackArticleGenerationFailed({
            story_id: videoCreator.story?.id,
            error_message: errMsg,
          });
        } else {
          EventTracker.trackSocialPostGenerationFailed({
            story_id: videoCreator.story?.id,
            error_message: errMsg,
          });
        }
      }
    };

    // Build generation details for versioning
    const buildGenerationDetails = () => ({
      templateName: latestTemplateNameRef.current,
      language: generatedContentLanguage || undefined,
      speakers: selectedSpeakers.map(s => s.name || s.speakerLabel),
      goal: selectedGoal?.label,
      audiences: selectedAudiences.map(a => a.label),
    });

    const handleSelectVersion = (
      version: ContentVersion,
      keepComparing?: boolean,
    ) => {
      selectVersion(version);
      setContentHtmlProgrammatic(version.content);
      // Only reset comparison if not explicitly keeping it, and if switching to latest version
      if (!keepComparing && version.id === latestVersion?.id) {
        setIsComparing(false);
      }
      videoCreator.contentStudioGeneratedContent = {
        ...videoCreator.contentStudioGeneratedContent,
        [typeKey]: {
          ...data,
          hasBeenGenerated: true,
          content: {
            ...(data?.content || {}),
            response: version.content,
          },
        },
      } as ContentViewData;
    };

    const handleToggleCompare = () => {
      setIsComparing(prev => !prev);
    };

    // Generate diff HTML when comparing
    // Only show diff if explicitly comparing AND we have multiple versions loaded
    // Also require isInitialized to prevent flash on initial mount
    const displayHtml =
      isInitialized &&
      isComparing &&
      isViewingPastVersion &&
      latestVersion &&
      contentHtml &&
      activeArticleVersions.length > 1
        ? generateDiffHtml(contentHtml, latestVersion.content)
        : contentHtml;

    const contentStats = useMemo(
      () => getContentStats(contentHtml),
      [contentHtml],
    );

    const [revisionModalOpen, setRevisionModalOpen] = useState(false);
    const handleRegenerate = async (
      revision: string,
      options: {
        selectedLanguage?: string | null;
        voiceOption?: 'brand' | Speaker;
      },
    ) => {
      latestPromptRef.current = revision;
      // Flush any pending autosave so the draft is durably persisted before
      // the AI output overwrites it. The AI output itself is recorded as an
      // `ai_revision` version by `endAIGeneration`, so the prior working
      // state remains reachable through the normal autosave round-trip.
      if (contentHtml) {
        await autosave.flush();
      }
      startAIGeneration(true, revision); // Track revision state to suppress user edit versions
      setImageSize(null);
      setIsLoading(true);
      setIsRegenerating(true);
      setRevisionModalOpen(false);
      {
        const regenProps = {
          story_id: story?.id,
          story_title: videoCreator.story?.title,
          template: promptTemplate?.title,
          custom_instructions: customPrompt,
          revision,
          selected_language:
            options.selectedLanguage ?? generatedContentLanguage ?? undefined,
          selected_topics: latestSelectedTopicsRef.current,
        };
        if (type === 'blog') {
          EventTracker.trackArticleRegenerationStarted(regenProps);
        } else {
          EventTracker.trackSocialPostRegenerationStarted(regenProps);
        }
      }

      // Determine speakers based on voice option
      const speakersForRegeneration =
        options.voiceOption && options.voiceOption !== 'brand'
          ? [options.voiceOption as Speaker]
          : selectedSpeakers;

      // Build revision with first-person perspective instruction when a specific speaker is selected
      let finalRevision = revision;
      if (options.voiceOption && options.voiceOption !== 'brand') {
        const speaker = options.voiceOption as Speaker;
        const speakerName = speaker.name || speaker.speakerLabel;
        const firstPersonInstruction = `Write this article from ${speakerName}'s first-person perspective. When the content is about ${speakerName}, use "I", "my", and "me" as if ${speakerName} is the author sharing their own experiences and insights. When referring to other people in the conversation, use their names or appropriate third-person pronouns.`;
        finalRevision = `${firstPersonInstruction}\n\n${revision}`;
      }

      // Parse length-change intent and inject word count instructions.
      // The overriddenTargetWordCount persists the user's intended target across
      // revisions (e.g., "Make it 500 words" → "Change the tone" still respects 500).
      const lengthIntent = parseRevisionLengthIntent(
        revision,
        contentStats.words,
        lengthTarget,
        contentStats.chars,
      );
      finalRevision += buildLengthInstruction(
        lengthIntent,
        contentStats.words,
        lengthTarget,
        contentStats.chars,
      );
      if (lengthIntent) {
        setLengthTarget({
          metric: lengthIntent.metric,
          target: lengthIntent.target,
        });
      }

      await gptService.regenerateStreamResponse({
        key: promptKey,
        type: 'regenerate',
        setLoading: setIsLoading,
        onError: handleGenerationError,
        template: promptTemplate,
        customInstructions: customPrompt,
        revision: finalRevision,
        selectedSpeakers: speakersForRegeneration,
        selectedGoal,
        selectedAudiences,
        selectedLanguage: options.selectedLanguage || generatedContentLanguage,
      });
    };

    // The editor region has nothing to show until the first chunk of the stream
    // lands, so it — and only it — carries the generating indicator. The page
    // shell (back, switcher, versions, chat) stays visible and usable; this used
    // to be a FullScreenLoadingOverlay, which blacked out the whole app for a
    // wait scoped to one panel (PLAT-11232).
    const isAwaitingGeneration = isLoading && !contentHtml && !isRegenerating;

    return (
      <>
        <Panel ref={panelRef} data-testid="articles-section">
          <ContentActions
            renderLeft={
              !showArticlesList
                ? () => (
                    <BackWithSwitcher>
                      <BackButton
                        onClick={() => {
                          const hasUnsavedUserEdits =
                            contentHtml && isUserEditDirtyRef.current;
                          if (hasUnsavedUserEdits) {
                            setShowUnsavedDialog(true);
                          } else {
                            setShowArticlesList(true);
                            setOpenedFromTable(false);
                          }
                        }}
                      >
                        <BackArrow viewBox="0 0 24 24" fill="none">
                          <path
                            d="M15 18l-6-6 6-6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </BackArrow>
                      </BackButton>
                      {visibleEntries.length > 0 && (
                        <ItemSwitcher ref={switcherRef}>
                          {isEditingTitle ? (
                            <TitleEditInput
                              ref={titleInputRef}
                              value={editingTitleValue}
                              onChange={e =>
                                setEditingTitleValue(e.target.value)
                              }
                              onBlur={() =>
                                handleRenameCurrentTitle(editingTitleValue)
                              }
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  handleRenameCurrentTitle(editingTitleValue);
                                } else if (e.key === 'Escape') {
                                  setIsEditingTitle(false);
                                }
                              }}
                            />
                          ) : (
                            <TitleRow>
                              {/* The name opens the switcher, the pencil renames
                                  — opening the list is the far more frequent
                                  intent, so it takes the row's large target and
                                  the chevron's promise matches the text beside
                                  it. No aria-label: the accessible name is the
                                  article's own title, which is the useful thing
                                  to announce; the chevron carries the "All …"
                                  name for the same action. */}
                              <TitleClickArea
                                onClick={e => {
                                  e.stopPropagation();
                                  setSwitcherOpen(!switcherOpen);
                                }}
                                title={`All ${type === 'blog' ? 'articles' : 'social posts'}`}
                                aria-haspopup="listbox"
                                aria-expanded={switcherOpen}
                              >
                                <ItemSwitcherName>
                                  {parseArticleTitle(
                                    currentContent?.title || 'Untitled',
                                    type,
                                  )}
                                </ItemSwitcherName>
                              </TitleClickArea>
                              {/* Sibling of the title, not a child: a button
                                  cannot nest inside one, and the rename is now a
                                  real target rather than decoration. */}
                              <EditTitleButton
                                onClick={e => {
                                  e.stopPropagation();
                                  handleStartEditTitle();
                                }}
                                title={`Rename this ${type === 'blog' ? 'article' : 'post'}`}
                                aria-label={`Rename this ${type === 'blog' ? 'article' : 'post'}`}
                              >
                                <SimplePencilIcon width="16" height="16" />
                              </EditTitleButton>
                              <SwitcherChevron
                                onClick={e => {
                                  e.stopPropagation();
                                  setSwitcherOpen(!switcherOpen);
                                }}
                                title={`All ${type === 'blog' ? 'articles' : 'social posts'}`}
                                aria-label={`All ${type === 'blog' ? 'articles' : 'social posts'}`}
                                aria-haspopup="listbox"
                                aria-expanded={switcherOpen}
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                >
                                  <path
                                    d="M4 6L8 10L12 6"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </SwitcherChevron>
                            </TitleRow>
                          )}
                          {contentStudioEnableAutosave && !isEditingTitle && (
                            <SaveStatusGroup>
                              <AutosaveStatusIcon
                                status={autosave.status}
                                lastSavedAt={autosave.lastSavedAt}
                              />
                              <AutosaveStatusIndicator
                                status={autosave.status}
                                lastSavedAt={autosave.lastSavedAt}
                              />
                            </SaveStatusGroup>
                          )}
                          {switcherOpen && (
                            <ItemSwitcherDropdown>
                              <DropdownHeader>
                                Story{' '}
                                {type === 'blog' ? 'Articles' : 'Social Posts'}
                                <DropdownCount>
                                  {visibleEntries.length}
                                </DropdownCount>
                              </DropdownHeader>
                              <DropdownDivider />
                              {[...visibleEntries]
                                .sort((a, b) => {
                                  // Active article first, then newest first
                                  if (a.id === currentContent?.id) return -1;
                                  if (b.id === currentContent?.id) return 1;
                                  return b.id - a.id;
                                })
                                .map(entry => (
                                  <ItemSwitcherItem
                                    key={entry.id}
                                    $isActive={entry.id === currentContent?.id}
                                    onClick={() => {
                                      if (isUserEditDirtyRef.current) {
                                        setShowUnsavedDialog(true);
                                      } else {
                                        setCurrentContent({
                                          id: entry.id,
                                          title: entry.title,
                                          content: entry.content,
                                          username: entry.username,
                                        });
                                        setLastSavedContent(entry.content);
                                        resetDirtyState();
                                      }
                                      setSwitcherOpen(false);
                                    }}
                                    title={parseArticleTitle(entry.title, type)}
                                  >
                                    {parseArticleTitle(entry.title, type)}
                                  </ItemSwitcherItem>
                                ))}
                            </ItemSwitcherDropdown>
                          )}
                        </ItemSwitcher>
                      )}
                    </BackWithSwitcher>
                  )
                : showArticlesList && visibleEntries.length > 0
                  ? () => (
                      <SavedLabel>
                        Saved {type === 'blog' ? 'Articles' : 'Social Posts'} (
                        {visibleEntries.length})
                      </SavedLabel>
                    )
                  : undefined
            }
            hasBeenGenerated={false}
            generateTestId="articles-generate-button"
            onGenerate={
              contentStudioContentProducingEnableGenerate
                ? () => {
                    if (type === 'blog') {
                      EventTracker.trackArticleProducerOpened({
                        story_id: story?.id,
                      });
                    } else {
                      EventTracker.trackSocialPostProducerOpened({
                        story_id: story?.id,
                      });
                    }
                    setAiProducerOpen(true);
                  }
                : undefined
            }
            alwaysShowGenerate
            generateVariant={showArticlesList ? 'primary' : 'outlined'}
            generateButtonLabel={showArticlesList ? undefined : 'Generate New'}
            renderShare={
              showArticlesList
                ? undefined
                : contentStudioEnableBlogShare && selectedContent?.type
                  ? () => (
                      <ContentShare
                        type={type === 'blog' ? 'Blog' : 'Social Post'}
                        onCopy={() => handleCopy('html')}
                        onCopyText={() => handleCopy('text')}
                      />
                    )
                  : undefined
            }
            /* Autosave status (icon + text) lives inline next to the title —
               see SaveStatusGroup inside TitleRow above. Not duplicated here. */
            onSave={
              showArticlesList || contentStudioEnableAutosave
                ? undefined
                : contentStudioEnableBlogSave && selectedContent?.type
                  ? handleSave
                  : undefined
            }
            saveDisabled={
              isSaving || (lastSavedContent !== null && !isEditorDirty)
            }
            saveButtonLabel={
              isSaving
                ? 'Saving...'
                : lastSavedContent !== null
                  ? 'Update'
                  : 'Save'
            }
          />

          {showArticlesList && Boolean(entries?.length) && (
            <ArticlesTable
              type={type}
              entries={entries}
              hideSectionTitle
              setCurrentContent={setCurrentContent}
              onSelectArticle={articleContent => {
                setOpenedFromTable(true);
                setShowArticlesList(false);
                setLastSavedContent(articleContent);
                resetDirtyState();
              }}
              onDelete={async contentId => {
                await handleDeleteBlogContent(
                  videoCreator,
                  datoClientStore.storyRepository,
                  contentId,
                  currentContent?.id,
                  () => {},
                  () => {},
                  setCurrentContent,
                  type === 'blog' ? 'savedBlog' : 'savedSocialPost',
                  type === 'blog' ? 'saved_blog' : 'saved_social_post',
                );
              }}
              onRename={handleRenameTitle}
            />
          )}

          {!showArticlesList && (contentHtml || isLoading) ? (
            <ContentLayout>
              <MainSection>
                <VersionTrackerContainer>
                  <ContentVersionTracker
                    versions={activeArticleVersions}
                    currentVersionId={currentVersionId}
                    onSelectVersion={handleSelectVersion}
                    onCompareToLatest={handleToggleCompare}
                    isComparing={isComparing}
                    onRenameVersion={renameVersion}
                    onDeleteVersion={(versionId: string) => {
                      deleteVersion(versionId);
                      videoCreator.showToast({
                        state: 'success',
                        message: 'Version deleted successfully',
                      });
                    }}
                    onSaveAsVersion={
                      canSaveAsVersion ? handleSaveAsVersion : undefined
                    }
                  />
                  {type === 'socialPost' ? (
                    <ModeToggle>
                      <ModeButton
                        active={viewMode === 'preview'}
                        onClick={() => setViewMode('preview')}
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                        >
                          <path
                            d="M1 7C1 7 3 3 7 3C11 3 13 7 13 7C13 7 11 11 7 11C3 11 1 7 1 7Z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <circle
                            cx="7"
                            cy="7"
                            r="2"
                            stroke="currentColor"
                            strokeWidth="1.5"
                          />
                        </svg>
                        Preview
                      </ModeButton>
                      <ModeButton
                        active={viewMode === 'edit'}
                        onClick={() => setViewMode('edit')}
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                        >
                          <path
                            d="M9.5 1.5L12.5 4.5L5 12H2V9L9.5 1.5Z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Edit
                      </ModeButton>
                    </ModeToggle>
                  ) : (
                    <div />
                  )}
                  <ContentStats>
                    <StatItem>
                      <strong>{contentStats.words.toLocaleString()}</strong>{' '}
                      words
                    </StatItem>
                    <StatDivider>|</StatDivider>
                    <StatItem>
                      <strong>{contentStats.chars.toLocaleString()}</strong>{' '}
                      chars
                    </StatItem>
                  </ContentStats>
                </VersionTrackerContainer>
                <Main
                  ref={editorScrollRef}
                  $previewMode={type === 'socialPost' && viewMode === 'preview'}
                >
                  {isAwaitingGeneration ? (
                    <GeneratingRegion
                      aria-label={`Generating ${
                        type === 'blog' ? 'article' : 'social post'
                      }…`}
                      role="status"
                    >
                      <SkeletonContent>
                        <SkeletonDocument />
                      </SkeletonContent>
                    </GeneratingRegion>
                  ) : (
                    <Content
                      $previewMode={
                        type === 'socialPost' && viewMode === 'preview'
                      }
                    >
                      {isComparing && displayHtml ? (
                        <DiffView
                          dangerouslySetInnerHTML={{ __html: displayHtml }}
                        />
                      ) : type === 'socialPost' && viewMode === 'preview' ? (
                        <PreviewOnlyContainer>
                          <SocialPreview
                            content={contentHtml}
                            authorName={videoCreator.organization?.title}
                            avatarUrl={videoCreator.organization?.logo?.url}
                            imageUrl={videoCreator.organization?.mainImage?.url}
                            channelSlug={channelSlug}
                          />
                        </PreviewOnlyContainer>
                      ) : (
                        <BlogEditor
                          content={contentHtml}
                          onEditorDirty={() => {
                            // Ignore onChange events during/right after AI generation or programmatic
                            // content loads — the editor fires onChange when content is set, not just
                            // on user interaction.
                            if (isLoading || isRegenerating) return;
                            if (
                              Date.now() < suppressEditorDirtyUntilRef.current
                            )
                              return;
                            setIsEditorDirty(true);
                            isUserEditDirtyRef.current = true;
                          }}
                          onContentUpdate={(html: string) => {
                            setContentHtml(html);
                            // Idle autosave — persists the draft in-place.
                            autosave.onContentChange(html);
                            videoCreator.contentStudioGeneratedContent = {
                              ...videoCreator.contentStudioGeneratedContent,
                              [typeKey]: {
                                ...data,
                                hasBeenGenerated: true,
                                content: {
                                  ...(data?.content || {}),
                                  response: html,
                                },
                              },
                            } as ContentViewData;
                          }}
                          disabled={isLoading}
                          contentType={type}
                        />
                      )}
                    </Content>
                  )}
                </Main>
              </MainSection>
              <ChatPanel>
                <ChatInterface
                  onSendMessage={handleRegenerate}
                  isLoading={isLoading}
                  currentWordCount={contentStats.words}
                  currentCharCount={contentStats.chars}
                  selectedLanguage={generatedContentLanguage}
                  latestVersionId={latestVersion?.id}
                  latestVersionLabel={latestVersion?.label}
                  onSelectVersion={versionId => {
                    const version = activeArticleVersions.find(
                      v => v.id === versionId,
                    );
                    if (version) handleSelectVersion(version, isComparing);
                  }}
                  generationDetails={
                    latestVersion?.generationDetails || {
                      templateName:
                        promptTemplate?.title ||
                        (customPrompt
                          ? customPrompt
                              .trim()
                              .split(/\s+/)
                              .slice(0, 6)
                              .join(' ')
                              .replace(/[.,;!?]+$/, '')
                          : undefined),
                      language: generatedContentLanguage || undefined,
                      speakers: selectedSpeakers.map(
                        s => s.name || s.speakerLabel,
                      ),
                      goal: selectedGoal?.label,
                      audiences: selectedAudiences.map(a => a.label),
                    }
                  }
                  speakers={transcriptSpeakers}
                  contentId={currentContent?.id}
                  disableAutoFocus={openedFromTable}
                />
              </ChatPanel>
            </ContentLayout>
          ) : null}
        </Panel>

        {showUnsavedDialog && (
          <Modal isOpen closeModal={() => setShowUnsavedDialog(false)}>
            <ModalWrapper>
              <ModalHeading accentColor="#f2f3f5">Unsaved Changes</ModalHeading>
              <ModalMessage>
                You have unsaved changes. If you leave this page, your progress
                will be lost.
              </ModalMessage>
              <ModalButtonsContainer>
                <ModalCancelButton
                  accentColor="#17c964"
                  onClick={() => setShowUnsavedDialog(false)}
                >
                  Stay on Page
                </ModalCancelButton>
                <ModalConfirmButton
                  accentColor="#ef4444"
                  onClick={() => {
                    setShowUnsavedDialog(false);
                    setShowArticlesList(true);
                    setOpenedFromTable(false);
                  }}
                >
                  Leave Without Saving
                </ModalConfirmButton>
              </ModalButtonsContainer>
            </ModalWrapper>
          </Modal>
        )}

        {aiProducerOpen ? (
          <AIProducerModal
            customType={
              contentStudioContentProducingEnableCustom
                ? type === 'blog'
                  ? 'Blog'
                  : 'Social Post'
                : undefined
            }
            title={`Let's write ${
              type === 'blog' ? 'an article' : 'a social post'
            }`}
            templates={
              (type === 'blog'
                ? templatedPromptsStore.aiBlogTemplates
                : templatedPromptsStore.aiSocialPostTemplates) || []
            }
            onClose={() => setAiProducerOpen(false)}
            onConfirm={handleGenerate}
            enableSpeakerSelection
          />
        ) : null}

        {revisionModalOpen ? (
          <BrandVoiceRevisionModal
            selectedLanguage={generatedContentLanguage}
            title={`Regenerate ${type === 'blog' ? 'Article' : 'Social Post'}`}
            onClose={() => setRevisionModalOpen(false)}
            handleGenerate={handleRegenerate}
          />
        ) : null}
      </>
    );
  },
);

export default BlogView;

const ModalMessage = styled.p`
  color: #a6aab3;
  font-size: 14px;
  text-align: center;
  margin: 0 0 16px;
  padding: 0 16px;
  line-height: 1.5;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 1px solid #484848;
  border-radius: 8px;
  color: #f3e9d7;
  cursor: pointer;
  padding: 6px;
  width: 36px;
  height: 36px;

  &:hover {
    border-color: #17c964;
    color: #17c964;
  }
`;

const BackArrow = styled.svg`
  width: 20px;
  height: 20px;
`;

const BackWithSwitcher = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ItemSwitcher = styled.div`
  position: relative;
  /* Flex so the title row and the autosave status group sit side-by-side
     instead of stacking. */
  display: flex;
  align-items: center;
  gap: 10px;
`;

/**
 * The rename trigger, a sibling of the title rather than a child of it — which
 * is what lets the title itself open the switcher.
 *
 * Transparent-but-present rather than `display: none`: revealing it used to
 * widen the row, which shoved the chevron ~20px right exactly as the pointer
 * approached it. Holding the space keeps the chevron still.
 */
const EditTitleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  color: var(--text-muted);
  flex-shrink: 0;
  border-radius: 4px;
  opacity: 0;
  transition:
    opacity 0.15s ease,
    background 0.15s ease,
    color 0.15s ease;

  &:hover {
    background: var(--surface-elevated);
    color: var(--foreground);
  }

  &:focus-visible {
    opacity: 1;
  }
`;

/* Hovering ANYWHERE on the row reveals the pencil — including while the pointer
   is over the chevron — so it is already showing by the time it is aimed at. */
const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  max-width: 460px;

  &:hover ${EditTitleButton} {
    opacity: 1;
  }
`;

/* Groups the autosave cloud + "Saved · N ago" text into one unit so they
   read as a single status signal (Google-Docs style). Negative left margin
   nudges the icon tight against the dropdown chevron without loosening
   TitleRow's own 2px gap. */
const SaveStatusGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-left: 4px;
  flex-shrink: 0;
`;

// Opens the switcher, like the chevron beside it: the name is what a creator
// aims at when they want the list, and it is the row's large target.
const TitleClickArea = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  color: var(--foreground);
  font-size: 15px;
  font-weight: var(--font-weight-semi-bold);
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.15s ease;
  min-width: 0;

  &:hover {
    background: var(--sidebar-hover);
  }
`;

const SwitcherChevron = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  flex-shrink: 0;
  transition: all 0.15s ease;

  &:hover {
    background: var(--surface-elevated);
    color: var(--foreground);
  }
`;

const ItemSwitcherName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TitleEditInput = styled.input`
  background: var(--surface);
  ${fieldStateCss}
  border-radius: var(--radius-input);
  color: var(--foreground);
  font-size: 15px;
  font-weight: var(--font-weight-semi-bold);
  padding: 4px 8px;
  width: 460px;
  outline: none;

  &::selection {
    background: var(--primary-tint);
  }
`;

const ItemSwitcherDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  background: var(--popover);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 4px;
  width: 410px;
  max-height: 240px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  animation: switcherSlide 0.15s ease-out;

  @keyframes switcherSlide {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const DropdownHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px 4px;
  font-size: 11px;
  font-weight: var(--font-weight-semi-bold);
  color: var(--text-muted);
  letter-spacing: 0.2px;
`;

const DropdownCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: var(--surface-elevated);
  font-size: 10px;
  font-weight: var(--font-weight-semi-bold);
  color: var(--text-secondary);
  letter-spacing: 0;
  text-transform: none;
`;

const DropdownDivider = styled.div`
  height: 1px;
  background: var(--border-subtle);
  margin: 4px 8px;
`;

const ItemSwitcherItem = styled.button<{ $isActive?: boolean }>`
  display: block;
  width: 100%;
  padding: 8px 10px;
  background: ${props =>
    props.$isActive ? 'var(--primary-tint)' : 'transparent'};
  border: none;
  border-radius: 6px;
  color: ${props => (props.$isActive ? 'var(--primary)' : 'var(--foreground)')};
  font-size: 13px;
  font-weight: ${props => (props.$isActive ? '600' : '400')};
  cursor: pointer;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: all 0.15s ease;

  &:hover {
    background: var(--sidebar-hover);
  }
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;

  .buttons {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
  }
`;

const ContentLayout = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 15px;
  margin-bottom: 8px;
  flex: 1;
  min-height: 0;
`;

const MainSection = styled.div`
  flex: 3;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const VersionTrackerContainer = styled.div`
  background: linear-gradient(
    135deg,
    var(--surface) 0%,
    var(--surface-subtle) 100%
  );
  border: 1px solid var(--border);
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  padding: 6px 16px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 16px;
`;

const ModeToggle = styled.div`
  display: flex;
  gap: 4px;
  background: var(--popover);
  padding: 2px;
  border-radius: 6px;
  justify-self: center;
`;

const ModeButton = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: none;
  background: ${props => (props.active ? 'var(--accent)' : 'transparent')};
  color: ${props =>
    props.active ? 'var(--background)' : 'var(--text-secondary)'};
  font-size: 12px;
  font-weight: 600;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: ${props =>
      props.active ? 'var(--accent)' : 'var(--sidebar-hover)'};
    color: ${props =>
      props.active ? 'var(--background)' : 'var(--text-secondary)'};
  }
`;

const ContentStats = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  color: var(--text-muted);
  font-size: 11px;
  justify-self: end;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const StatItem = styled.span`
  strong {
    color: var(--text-secondary);
    font-weight: 600;
  }
`;

const StatDivider = styled.span`
  color: var(--border);
`;

const SavedLabel = styled.span`
  color: var(--text-muted);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-normal);
  line-height: var(--leading-text-sm);
  margin: 0;
  white-space: nowrap;
`;

const Main = styled.div<{ $previewMode?: boolean }>`
  position: relative;
  border-radius: 0 0 8px 8px;
  border: 1px solid var(--border);
  border-top: none;
  flex: 1;
  min-height: 0;
  overflow: scroll;
  /* Feed-gray in Social Post preview so the LinkedIn card sits on its
     native-looking surface; white editor surface everywhere else. */
  background: ${({ $previewMode }) =>
    $previewMode ? '#f3f2ef' : 'var(--content-editor-background)'};
  padding: ${({ $previewMode }) => ($previewMode ? '0' : '0 16px')};

  &:first-child {
    border-radius: 8px;
    border-top: 1px solid var(--border);
  }
`;

// The editor surface before the first chunk lands: a document skeleton in
// the same box the text will stream into, so nothing moves when it does. The
// surface is white, so the skeleton takes a light fill rather than the app's
// dark `--surface-elevated`.
const GeneratingRegion = styled.div`
  --skeleton-fill: color-mix(
    in srgb,
    var(--content-editor-foreground) 9%,
    var(--content-editor-background)
  );

  min-height: 100%;
  background: var(--content-editor-background);
`;

/* Content's box, minus the editor chrome. */
const SkeletonContent = styled.div`
  max-width: 800px;
  margin: 0 auto 40px;
  padding: 10px 80px;
`;

const ChatPanel = styled.div`
  flex: 1;
  min-width: 280px;
  max-width: 350px;
`;

const Content = styled.div<{ $previewMode?: boolean }>`
  color: var(--content-editor-foreground);
  font-size: var(--text-sm);
  line-height: var(--leading-text-sm);
  margin: ${({ $previewMode }) => ($previewMode ? '0' : '0 auto 40px auto')};
  padding: ${({ $previewMode }) => ($previewMode ? '0' : '10px 80px')};
  border-radius: 5px;
  user-select: none;
  max-width: ${({ $previewMode }) => ($previewMode ? 'none' : '800px')};
  /* Swap to feed-gray only in Social Post preview; PreviewOnlyContainer
     (also #f3f2ef) now fills edge-to-edge with no white border around it. */
  background-color: ${({ $previewMode }) =>
    $previewMode ? '#f3f2ef' : 'var(--content-editor-background)'};
  display: flex;
  flex-direction: column;
  gap: 24px;
  .jodit-container {
    border: none !important;
    background-color: var(--content-editor-background) !important;
  }
  .jodit-workplace,
  .jodit-wysiwyg {
    background-color: var(--content-editor-background) !important;
    color: var(--content-editor-foreground) !important;
    font-size: var(--text-sm) !important;
    line-height: var(--leading-text-sm) !important;
  }
  h1 {
    color: var(--content-editor-foreground);
    font-size: var(--text-lg);
    line-height: var(--leading-text-lg);
    text-align: center;
  }
  h2 {
    color: var(--content-editor-foreground);
    font-size: var(--text-md);
    line-height: var(--leading-text-md);
  }
  h3 {
    color: var(--content-editor-foreground);
    font-size: var(--text-sm);
    line-height: var(--leading-text-sm);
  }
  p,
  li,
  span {
    color: var(--content-editor-foreground);
  }
  p strong {
    font-weight: 800;
  }
  & > p:has(img) {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  blockquote {
    font-style: italic;
    color: var(--content-editor-foreground);
    border-left: 3px solid var(--content-editor-foreground);
    padding-left: 16px;
  }
  a {
    color: var(--content-editor-foreground);
  }
`;

const PreviewOnlyContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 40px 20px;
  min-height: 600px;
  background: #f3f2ef;
`;

const DiffView = styled.div`
  width: 100%;
  min-height: 400px;
  background: var(--content-editor-background);
  color: var(--content-editor-foreground);
  padding: 20px;
  border-radius: 8px;

  blockquote {
    font-style: italic;
    color: var(--content-editor-foreground);
    border-left: 3px solid var(--content-editor-foreground);
    padding-left: 16px;
  }
`;
